import {
    IdentificationDocumentType,
    StartPersonalApplicationResponse,
    StartPersonalLeaseApplicationBody,
    CreditReportType,
    StartPersonalApplicationWithoutCreditReportResponse,
} from '@centecomm/portal-service';
import { useLeasing } from '@centecomm/portal-service-react';
import { useDispatch, useSelector } from 'react-redux';
import { LeasingActions, LeasingActionTypes } from 'State/Leasing/actions';
import selectors from 'State/Leasing/selectors';
import { LoadStatus } from 'Types';
import { convertURLBlobToFile } from 'utils';
import { Address, BusinessInformation, IdentificationType, LeasingApplication, PersonalInformation } from '../Types';
import { FCRA } from '../Types/FCRA';

type UseLeasingStateHookReturnProperties = () => {
    isLoaded: boolean;
    hasActiveApplication: boolean;
    applicationId?: number;

    isApplicationStarted: boolean;

    pauseActiveApplication: () => void;
    isApplicationPaused: boolean;

    creditReportType: CreditReportType;
    creditReportEnabled: boolean;

    personalInformation?: PersonalInformation;
    businessInformation?: BusinessInformation;
    setPersonalInformation: (personalInformation: PersonalInformation) => void;
    setBusinessInformation: (businessInformation: BusinessInformation) => void;
    address?: Address;
    setAddress: (address: Address) => void;
    identification?: IdentificationType;
    setIdentification: (ident: IdentificationType) => void;
    setFCRA: (fcraData: FCRA) => void;
    isFCRAAccepted: boolean;
    fcra: FCRA | undefined;

    startApplication: () => Promise<StartPersonalApplicationResponse | StartPersonalApplicationWithoutCreditReportResponse>;
};

const useLeasingState: UseLeasingStateHookReturnProperties = () => {
    const dispatch = useDispatch();

    const applicationLoadStatus: LoadStatus = useSelector(selectors.applicationLoadStatus);
    const activeApplication: LeasingApplication | undefined = useSelector(selectors.leasingApplication);

    const creditReportType: CreditReportType = useSelector(selectors.creditReportType) as CreditReportType;
    const creditReportEnabled: boolean = useSelector(selectors.creditReportEnabled) ?? false;
    const personalInformation = useSelector(selectors.personalInformation);
    const businessInformation = useSelector(selectors.businessInformation);
    const address = useSelector(selectors.address);
    const identification = useSelector(selectors.identification);
    const fcra = useSelector(selectors.fcra);
    const isFCRAAccepted = useSelector(selectors.hasAcceptedFCRA);

    const isApplicationStarted = useSelector(selectors.isApplicationStarted);
    const isApplicationPaused = useSelector(selectors.isApplicationPaused);

    const { startPersonalLeaseApplication, startPersonalLeaseApplicationWithoutCreditReport } = useLeasing();

    const pauseActiveApplication = () => {
        dispatch({
            type: LeasingActions.SET_PAUSE_APPLICATION,
        } as LeasingActionTypes);
    };

    // Personal Information
    const setPersonalInformation = (updatedPersonalInformation: PersonalInformation) => {
        dispatch({
            type: LeasingActions.SET_APPLICATION_PERSONAL_INFORMATION,
            payload: updatedPersonalInformation,
        });
    };

    const setBusinessInformation = (updatedBusinessInformation: BusinessInformation) => {
        dispatch({
            type: LeasingActions.SET_APPLICATION_BUSINESS_INFORMATION,
            payload: updatedBusinessInformation,
        });
    };

    const setAddress = (updatedAddress: Address) => {
        dispatch({
            type: LeasingActions.SET_APPLICATION_ADDRESS,
            payload: updatedAddress,
        });
    };

    const setIdentification = (updatedIdentification: IdentificationType): void => {
        dispatch({
            type: LeasingActions.SET_APPLICATION_IDENTIFICATION,
            payload: updatedIdentification,
        });
    };

    const setFCRA = (updatedFcra: FCRA): void => {
        dispatch({
            type: LeasingActions.SET_FCRA,
            payload: updatedFcra,
        });
    };

    const formatBusinessApplicationData = async (): Promise<StartPersonalLeaseApplicationBody> =>
        ({
            businessName: businessInformation?.businessName,
            firstName: businessInformation?.firstName,
            lastName: businessInformation?.lastName,
            ...(creditReportEnabled
                ? {
                      FCRAAcknowledgementName: `${fcra?.firstName} ${fcra?.lastName}`,
                  }
                : {}),
            phoneNumber: businessInformation?.phone.replace(/\D/g, ''),
            street: address?.street,
            street2: address?.street2,
            city: address?.city,
            state: address?.state,
            zipcode: address?.zip,
            creditReportType: CreditReportType.EquifaxBCR,
            taxId: businessInformation?.taxId,
        } as StartPersonalLeaseApplicationBody);

    const formatPersonalApplicationData = async (): Promise<StartPersonalLeaseApplicationBody> => {
        const identificationImages: File[] = [];
        try {
            if (identification) {
                if (identification.passportNumber) {
                    const imagePassportId = await convertURLBlobToFile(
                        identification?.uploadPassportId as { name: string; url: string; type?: string | undefined },
                    );

                    identificationImages.push(imagePassportId);
                } else {
                    const imageIdFront = await convertURLBlobToFile(
                        identification?.uploadIdFront as { name: string; url: string; type?: string | undefined },
                    );
                    identificationImages.push(imageIdFront);

                    if (identification.uploadIdBack) {
                        const imageIdBack = await convertURLBlobToFile(
                            identification?.uploadIdBack as { name: string; url: string; type?: string | undefined },
                        );
                        identificationImages.push(imageIdBack);
                    }
                }
            }
        } catch (e) {
            throw new Error('There was an issue uploading your documents. Please try again, or refresh.');
        }

        return {
            firstName: personalInformation?.firstName,
            lastName: personalInformation?.lastName,
            phoneNumber: personalInformation?.phone.replace(/\D/g, ''),
            ...(creditReportEnabled
                ? {
                      socialSecurityNumber: personalInformation?.socialSecurityNumber.replace(/\D/g, ''),
                      FCRAAcknowledgementName: `${fcra?.firstName} ${fcra?.lastName}`,
                  }
                : {}),
            dateOfBirth: personalInformation?.dateOfBirth,
            street: address?.street,
            street2: address?.street2,
            city: address?.city,
            state: address?.state,
            zipcode: address?.zip,
            identificationDocumentExpiration: identification?.passportNumber
                ? identification?.passportExpirationDate
                : identification?.stateIdExpirationDate,
            // only have this property if identification type is state id
            ...(!identification?.passportNumber ? { identificationDocumentState: identification?.stateOfIssue } : {}),
            identificationDocumentType: identification?.passportNumber
                ? IdentificationDocumentType.Passport
                : IdentificationDocumentType.StateIdentification,
            identificationDocumentImages: identificationImages,
            identificationDocumentNumber: identification?.passportNumber ?? identification?.identificationNumber,
            creditReportType: CreditReportType.EquifaxCCR,
        } as StartPersonalLeaseApplicationBody;
    };

    const startApplication = async (): Promise<
        StartPersonalApplicationResponse | StartPersonalApplicationWithoutCreditReportResponse
    > => {
        if (!activeApplication) throw new Error('Unable to start application with an undefined application');

        const applicationDataPromise =
            creditReportType === CreditReportType.EquifaxCCR
                ? formatPersonalApplicationData()
                : formatBusinessApplicationData();

        const applicationData = await applicationDataPromise;

        const apiCallFn = activeApplication.creditReportEnabled
            ? startPersonalLeaseApplication
            : startPersonalLeaseApplicationWithoutCreditReport;

        return apiCallFn(activeApplication.id, applicationData);
    };

    return {
        isLoaded: applicationLoadStatus === LoadStatus.LOADED,
        hasActiveApplication: activeApplication != null,
        applicationId: activeApplication?.id,
        isApplicationStarted,
        pauseActiveApplication,
        isApplicationPaused,
        creditReportType,
        creditReportEnabled,
        personalInformation,
        businessInformation,
        setPersonalInformation,
        setBusinessInformation,
        fcra,
        setFCRA,
        isFCRAAccepted,
        address,
        setAddress,
        identification,
        setIdentification,
        startApplication,
    };
};

export default useLeasingState;
